import { Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../hooks/useNotifications';
import { formatPostDate } from '../utils/dateFormatter';
import { Button } from '../components/ui';

const NOTIFICATION_LABELS = {
  like: (p) => `${p.actorUserName || 'Alguien'} le gustó tu publicación`,
  comment: (p) => `${p.actorUserName || 'Alguien'} comentó en tu publicación`,
  order_request: (p) => `${p.clientName || 'Un cliente'} solicitó tu servicio: ${(p.serviceName || '').slice(0, 40)}${(p.serviceName || '').length > 40 ? '...' : ''}`,
  order_accepted: (p) => `${p.sellerName || 'El vendedor'} aceptó tu solicitud`,
  order_rejected: (p) => `${p.sellerName || 'El vendedor'} rechazó tu solicitud`,
  order_payment_requested: (p) => `${p.sellerName || 'El vendedor'} solicitó el pago de tu servicio`,
  order_in_progress: (p) => `${p.sellerName || 'El vendedor'} comenzó a trabajar en tu pedido`,
  order_completed: (p) => `${p.sellerName || 'El vendedor'} marcó tu pedido como completado`,
  order_deliverables_added: (p) => `${p.sellerName || 'El vendedor'} envió ${p.fileCount ?? 0} archivo(s) para tu pedido`,
  new_follower: (p) => `${p.followerName || 'Alguien'} te siguió`,
  order_message: (p) => `Chat de ${(p.serviceName || 'servicio').slice(0, 40)}${p.serviceName && p.serviceName.length > 40 ? '...' : ''} (${p.messageCount || 1})`
};

const NOTIFICATION_ICONS = {
  like: (
    <svg className="w-5 h-5 text-pink-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  ),
  comment: (
    <svg className="w-5 h-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  ),
  order_request: (
    <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
    </svg>
  ),
  order_accepted: (
    <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  order_payment_requested: (
    <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-10V6m0 12v-2m9-4a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  order_rejected: (
    <svg className="w-5 h-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  order_in_progress: (
    <svg className="w-5 h-5 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  order_completed: (
    <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  order_deliverables_added: (
    <svg className="w-5 h-5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
    </svg>
  ),
  order_message: (
    <svg className="w-5 h-5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  )
};

const NotificationsPage = () => {
  const { currentUser } = useAuth();
  const {
    notifications,
    unreadCount,
    loading,
    loadMore,
    hasMore,
    markAsRead,
    markAllAsRead
  } = useNotifications(currentUser?.uid);

  const getLink = (n) => {
    if (n.type === 'like' || n.type === 'comment') return '/';
    if (n.type === 'new_follower') return '/profile/' + (n.payload?.followerId || '');
    return '/services';
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50 dark:bg-slate-900">
        <div className="container-custom py-8">
          <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                Notificaciones
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                {unreadCount > 0 ? `${unreadCount} sin leer` : 'Todo al día'}
              </p>
            </div>
            {unreadCount > 0 && (
              <Button variant="outline" size="sm" onClick={() => markAllAsRead()}>
                Marcar todas como leídas
              </Button>
            )}
          </div>

          <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden">
            {loading && notifications.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                Cargando notificaciones...
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-12 text-center text-gray-500 dark:text-gray-400">
                No tienes notificaciones
              </div>
            ) : (
              <ul className="divide-y divide-gray-200 dark:divide-slate-700">
                {notifications.map((n) => {
                  const labelFn = NOTIFICATION_LABELS[n.type];
                  const label = labelFn && n.payload ? labelFn(n.payload) : 'Nueva notificación';
                  const icon = NOTIFICATION_ICONS[n.type] || NOTIFICATION_ICONS.order_request;
                  const linkTo = getLink(n);
                  return (
                    <li key={n.id}>
                      <Link
                        to={linkTo}
                        onClick={() => !n.read && markAsRead(n.id)}
                        className={`flex items-start gap-4 px-4 py-4 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors ${
                          !n.read ? 'bg-indigo-50/50 dark:bg-indigo-900/10' : ''
                        }`}
                      >
                        <span className="flex-shrink-0 mt-0.5">{icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {label}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {formatPostDate(n.createdAt)}
                          </p>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
            {hasMore && notifications.length > 0 && (
              <div className="p-4 border-t border-gray-200 dark:border-slate-700 text-center">
                <Button variant="outline" size="sm" onClick={loadMore} loading={loading}>
                  Cargar más
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default NotificationsPage;
